import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuClipboardList, LuLoaderCircle, LuPlus, LuSave, LuTrash2 } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import useSmoText from '../smoText';

/** "12 / 40 kayıt (30 mevcut) (2 yeni)" pill of the manual data entry. */
export const EntryCount = ({ shown, total, filtered, existing, added }) => {
  const { label } = useSmoText();
  return (
    <span className="tw-inline-flex tw-h-9 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-sky-500/10 tw-px-3 tw-text-xs tw-font-semibold tw-text-sky-800 dark:tw-text-sky-200">
      <LuClipboardList aria-hidden="true" className="tw-h-4 tw-w-4" />
      {filtered ? label('entry.countFiltered', { shown, total }) : label('entry.count', { count: total })}
      {existing > 0 && <span className="tw-font-normal">{label('entry.existingCount', { count: existing })}</span>}
      {added > 0 && <span className="tw-font-normal">{label('entry.newCount', { count: added })}</span>}
    </span>
  );
};

EntryCount.propTypes = { shown: PropTypes.number.isRequired, total: PropTypes.number.isRequired, filtered: PropTypes.bool, existing: PropTypes.number, added: PropTypes.number };

/** Footer summary: rows plus existing / new badges and what is waiting for the save. */
export const EntryFooterInfo = ({ shown, total, filtered, existing, added, changed = 0, removed = 0 }) => {
  const { label } = useSmoText();
  return (
    <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
      <span>{filtered ? label('entry.rowsFiltered', { shown, total }) : label('entry.rows', { count: total })}</span>
      {existing > 0 && <span className="tw-rounded tw-bg-emerald-500/15 tw-px-1.5 tw-py-0.5 tw-font-semibold tw-text-emerald-800 dark:tw-text-emerald-200">{label('entry.existingBadge', { count: existing })}</span>}
      {added > 0 && <span className="tw-rounded tw-bg-amber-400/25 tw-px-1.5 tw-py-0.5 tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">{label('entry.newBadge', { count: added })}</span>}
      {changed > 0 && <span className="tw-rounded tw-bg-sky-500/15 tw-px-1.5 tw-py-0.5 tw-font-semibold tw-text-sky-800 dark:tw-text-sky-200">{label('entry.changedBadge', { count: changed })}</span>}
      {removed > 0 && <span className="tw-rounded tw-bg-rose-500/15 tw-px-1.5 tw-py-0.5 tw-font-semibold tw-text-rose-800 dark:tw-text-rose-200">{label('entry.removedBadge', { count: removed })}</span>}
    </span>
  );
};

EntryFooterInfo.propTypes = { ...EntryCount.propTypes, changed: PropTypes.number, removed: PropTypes.number };

/** "Tümünü Kaydet": asks first when the save deletes visits for good. */
export const EntrySaveButton = ({ pending, saving, disabled, onSave }) => {
  const { label } = useSmoText();
  const [confirming, setConfirming] = useState(false);
  const start = () => (pending.removed > 0 ? setConfirming(true) : onSave());
  return (
    <>
      <Button variant="primary" onClick={start} disabled={saving || disabled || !pending.any}>
        {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
        {saving ? label('entry.saving') : label('entry.saveAll')}
      </Button>
      <ConfirmDialog
        isOpen={confirming}
        tone="danger"
        icon={LuTrash2}
        title={label('entry.confirmDelete.title')}
        message={label('entry.confirmDelete.message', { count: pending.removed })}
        confirmLabel={label('entry.confirmDelete.confirm')}
        cancelLabel={label('entry.cancel')}
        onCancel={() => setConfirming(false)}
        onConfirm={() => { setConfirming(false); onSave(); }}
      />
    </>
  );
};

EntrySaveButton.propTypes = {
  pending: PropTypes.shape({ added: PropTypes.number, changed: PropTypes.number, removed: PropTypes.number, any: PropTypes.bool }).isRequired,
  saving: PropTypes.bool,
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

/** Loading / empty state of an entry table, with an optional "add row" action. */
export const EntryEmpty = ({ loading, icon: Icon = LuClipboardList, text, actionLabel, onAction }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-px-6 tw-py-14 tw-text-center">
    {loading
      ? <LuLoaderCircle aria-hidden="true" className="tw-h-8 tw-w-8 tw-animate-spin tw-text-teal-500" />
      : <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-2xl tw-bg-slate-500/10 tw-text-slate-400"><Icon aria-hidden="true" className="tw-h-6 tw-w-6" /></span>}
    <p className="tw-max-w-md tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{text}</p>
    {actionLabel && (
      <Button size="sm" variant="success" onClick={onAction}>
        <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
        {actionLabel}
      </Button>
    )}
  </div>
);

EntryEmpty.propTypes = { loading: PropTypes.bool, icon: PropTypes.elementType, text: PropTypes.string.isRequired, actionLabel: PropTypes.string, onAction: PropTypes.func };
